import { CategorisedStaticBenefits } from '@type/job-add';
import {
  BanknotesIcon, BriefcaseIcon, BookOpenIcon, HeartIcon, SunIcon,
} from '@heroicons/react/24/outline';

/** Types */
type CategorisedStaticBenefitsFn = (
  apiStaticBenefits: ApiSchema<'BenefitRead'>[] | undefined,
) => CategorisedStaticBenefits[] | undefined;

/**
 * @description Categorise static benefits.
 * - `locale` - The current app locale.
 * - `apiStaticBenefits` - The static benefits.
 */
export const categoriseStaticBenefits: CategorisedStaticBenefitsFn = (apiStaticBenefits) => {
  if (!apiStaticBenefits) return undefined;

  // Get categories titles
  const categoriesTitles = new Set(apiStaticBenefits?.map((benefit) => benefit.type) || []);

  return Array.from(categoriesTitles).map((category) => {
    const benefitsInCategory = apiStaticBenefits?.filter((benefit) => benefit.type === category) || [];
    const categoryTypeId = benefitsInCategory[0].typeId;

    // Get category icon switch
    let categoryIcon;
    const iconClass = 'size-4.5 shrink-0';
    switch (categoryTypeId) {
      case 1:
        categoryIcon = <BanknotesIcon className={iconClass} />;
        break;
      case 2:
        categoryIcon = <HeartIcon className={iconClass} />;
        break;
      case 3:
        categoryIcon = <BookOpenIcon className={iconClass} />;
        break;
      case 4:
        categoryIcon = <BriefcaseIcon className={iconClass} />;
        break;
      case 5:
        categoryIcon = <SunIcon className={iconClass} />;
        break;
      default:
        break;
    }

    return {
      category,
      icon: categoryIcon,
      type: categoryTypeId,
      benefits: benefitsInCategory,
    };
  });
};


/** Types */
type SelectedBenefitsFn = (
  selectedIds?: number[],
  categorisedBenefits?: CategorisedStaticBenefits[],
) => CategorisedStaticBenefits[] | undefined;

/**
 * @description Filter the selected benefits. Used to filter the selected benefits from the categorised benefits.
 * If the group has no benefits it will be removed from the array.
 * - `selectedIds` - The selected benefit ids.
 * - `categorisedBenefits` - The categorised benefits.
 */
export const filterSelectedBenefits: SelectedBenefitsFn = (selectedIds, categorisedBenefits) => {
  if (!selectedIds || !categorisedBenefits) return undefined;

  return categorisedBenefits.map((group) => {
    const selectedBenefits = group.benefits?.filter((benefit) => (benefit?.id ? selectedIds?.includes(benefit?.id) : false));
    return {
      ...group,
      benefits: selectedBenefits,
    };
  }).filter((group) => group.benefits?.length);
};

/**
 * @description Hide unselected benefits. Used to hide the benefits that are not selected.
 * If the group has no selected benefits it will also be hidden.
 * - `selectedIds` - The selected benefit ids.
 * - `categorisedBenefits` - The categorised benefits.
 */
export const hideUnselectedBenefits: SelectedBenefitsFn = (selectedIds, categorisedBenefits) => {
  if (!selectedIds || !categorisedBenefits) return undefined;

  return categorisedBenefits.map((group) => {
    // Add hidden flag to group if no selected benefits
    const hasSelectedBenefits = group.benefits?.some((benefit) => (benefit?.id ? selectedIds?.includes(benefit?.id) : false));

    // Check each benefit and add hidden flag if not selected
    const newBenefits = group.benefits?.map((benefit) => {
      if (benefit?.id && !selectedIds.includes(benefit?.id)) {
        return {
          ...benefit,
          hidden: true,
        };
      }
      return benefit;
    });

    // Return group with hidden flag if no selected benefits
    return {
      ...group,
      benefits: newBenefits,
      hidden: !hasSelectedBenefits,
    };
  });
};
